import {
  matchesText,
  type ActivityComposerContext,
  type ActivityComposerKind,
  type CommandPaletteGroup,
  type CommandPaletteItem,
} from '@solum/ui'
import type { PaletteScope } from './paletteScope'

export const ACTIVITY_COMMANDS: ReadonlyArray<{
  kind: ActivityComposerKind
  label: string
  detail: string
}> = [
  { kind: 'task', label: 'Add task', detail: 'Accountable work' },
  { kind: 'appointment', label: 'Add appointment', detail: 'Schedule time and participants' },
  { kind: 'call', label: 'Log phone call', detail: 'Record a conversation' },
  { kind: 'email', label: 'Log email', detail: 'Record a message' },
  { kind: 'note', label: 'Add note', detail: 'Capture internal context' },
  { kind: 'meeting_report', label: 'Create meeting report', detail: 'Minutes, decisions and follow-up' },
]

export interface ComposeDoGroupsOptions {
  query: string
  /** The already-governed invoker scope. Null means the palette was widened. */
  scope: PaletteScope | null
  context: ActivityComposerContext | null
  close: () => void
  openActivity: (request: { kind: ActivityComposerKind; context: ActivityComposerContext | null }) => void
}

/**
 * A declared record scope is an explicit user context and therefore outranks
 * the palette's merely highlighted search result. The latter becomes context
 * only on an unscoped surface (or after Tab promotes it into an explicit
 * scope). This prevents initial focus from silently retargeting an action.
 */
export function activityContextFor(
  scope: PaletteScope | null,
  scopeTitle: string,
  candidate: CommandPaletteItem['record'],
): ActivityComposerContext | null {
  if (scope?.recordId) return { entity: scope.entity, id: scope.recordId, label: scopeTitle }
  return candidate
    ? { entity: candidate.entity, id: candidate.id, label: candidate.label }
    : null
}

/**
 * Compose Do-mode commands in task order, without inventing any entity action.
 *
 * The invoker owns `scope.create` and `scope.verbs`; this function only ranks
 * those permitted commands ahead of the cross-cutting ActivityComposer verbs.
 * `@solum/ui` then renders these groups in the supplied order.
 */
export function composeDoGroups({
  query, scope, context, close, openActivity,
}: ComposeDoGroupsOptions): CommandPaletteGroup[] {
  const groups: CommandPaletteGroup[] = []

  if (scope) {
    const verbs = [...(scope.create ? [scope.create] : []), ...(scope.verbs ?? [])]
      .filter((verb) => matchesText(query, verb.label))
      .map((verb, index) => ({
        id: `verb:${index}:${verb.label}`,
        label: verb.label,
        /* The scope, never "N selected" (#2849): no palette verb acts on the
           selection — a collection act runs with no target, a create or an
           "Add to …" ignores it. The footer says how many are selected. */
        detail: scope.label,
        disabled: verb.disabled,
        disabledReason: verb.disabledReason,
        onSelect: verb.run,
      }))
    if (verbs.length) groups.push({
      id: 'scope-actions', label: `${scope.label} - actions`, testId: 'pal-scope-actions', items: verbs,
    })
  }

  const activities = ACTIVITY_COMMANDS
    .filter((item) => matchesText(query, item.label, item.detail))
    .map((item) => ({
      id: `activity:${item.kind}`,
      label: item.label,
      detail: context ? `${item.detail} · ${context.label}` : item.detail,
      onSelect: () => {
        close()
        openActivity({ kind: item.kind, context })
      },
    }))
  if (activities.length) groups.push({
    id: 'activities', label: 'Activities', testId: 'pal-activity', items: activities,
  })

  return groups
}
